/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * useVersionCheck Hook - React hook for version checking
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import versionService from '../services/versionService'
import { versionConfig } from '../config/versionConfig'

import { createLogger } from '../utils/logger'

const logger = createLogger('useVersionCheck')

/**
 * Hook for checking application version updates
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether to enable version checking
 * @param {boolean} options.autoStart - Whether to start polling automatically
 * @param {Function} options.onUpdate - Callback when update is detected
 * @returns {Object} Version check state and controls
 */
export function useVersionCheck(options = {}) {
  const { enabled = versionConfig.enabled, autoStart = false, onUpdate } = options

  const [updateInfo, setUpdateInfo] = useState(null)
  const [isChecking, setIsChecking] = useState(false)
  const [lastChecked, setLastChecked] = useState(null)

  // Use ref to avoid effect re-running when onUpdate callback changes
  const onUpdateRef = useRef(onUpdate)
  useEffect(() => {
    onUpdateRef.current = onUpdate
  }, [onUpdate])

  // Check for updates
  const checkForUpdate = useCallback(async () => {
    if (!enabled || isChecking) return null

    setIsChecking(true)

    try {
      const update = await versionService.checkForUpdate()

      if (update) {
        setUpdateInfo(update)

        if (onUpdateRef.current) {
          onUpdateRef.current(update)
        }
      }

      setLastChecked(new Date())
      return update
    } catch (error) {
      logger.error('[useVersionCheck] Error checking for update:', error)
      return null
    } finally {
      setIsChecking(false)
    }
  }, [enabled, isChecking])

  // Perform hard reload
  const performUpdate = useCallback(() => {
    versionService.hardReload()
  }, [])

  // Dismiss the update notification (only for non-forced updates)
  const dismissUpdate = useCallback(() => {
    if (updateInfo?.newBuildId && !updateInfo?.forceUpdate) {
      versionService.dismissUpdate(updateInfo.newBuildId)
    }
    setUpdateInfo(null)
  }, [updateInfo])

  // Subscribe to version service updates
  useEffect(() => {
    if (!enabled) return

    // Initialize version service
    versionService.init()

    // Subscribe to updates
    const unsubscribe = versionService.subscribe((update) => {
      // Don't show dismissed updates unless it's a forced update
      if (!update.forceUpdate && versionService.isDismissed(update.newBuildId)) {
        return
      }

      setUpdateInfo(update)

      if (onUpdateRef.current) {
        onUpdateRef.current(update)
      }
    })

    // Start polling if autoStart is enabled
    if (autoStart) {
      versionService.startPolling()
    }

    return () => {
      unsubscribe()
      if (autoStart) {
        versionService.stopPolling()
      }
    }
  }, [enabled, autoStart]) // Removed onUpdate from deps, using ref instead

  return {
    // State
    updateInfo,
    isChecking,
    lastChecked,
    hasUpdate: !!updateInfo,

    // Update characteristics
    forceUpdate: updateInfo?.forceUpdate || false,
    updatePriority: updateInfo?.updatePriority || 'recommended',

    // Current build info
    currentBuildId: versionConfig.buildId,
    newBuildId: updateInfo?.newBuildId,

    // Actions
    checkForUpdate,
    performUpdate,
    dismissUpdate,

    // Polling controls
    startPolling: () => versionService.startPolling(),
    stopPolling: () => versionService.stopPolling()
  }
}

export default useVersionCheck
